import React from "react";
import { Sparkles, ArrowRight } from "lucide-react";
import { MODEL_FAMILIES } from "../constants/models";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startLogin() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const families = ["openai", "anthropic", "gemini"];

export default function Login() {
  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-[#F9F9F7]" data-testid="login-page">
      {/* Left: brand / copy */}
      <div className="flex flex-col justify-between p-8 sm:p-12 lg:p-16">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-[#1F2421] flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-white" strokeWidth={2} />
          </div>
          <span className="font-heading text-xl font-bold tracking-tight text-[#171716]">OmniAI</span>
        </div>

        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#E6E6DF] bg-white px-3 py-1.5 mb-8">
            <span className="h-1.5 w-1.5 rounded-full bg-[#2D6A4F]" />
            <span className="text-xs font-medium text-[#5F5F5B]">Every frontier model. One workspace.</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#171716]">
            All your AI models.<br />
            <span className="text-[#2D6A4F]">In a single chat.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#5F5F5B] leading-relaxed max-w-md">
            Chat with GPT, Claude and Gemini side by side, generate images, and keep every conversation in one clean, searchable place.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            {families.map((f) => {
              const fam = MODEL_FAMILIES[f];
              return (
                <div
                  key={f}
                  className="flex items-center gap-2 rounded-full px-4 py-2 border"
                  style={{ background: fam.badgeBg, borderColor: fam.border }}
                  data-testid={`family-chip-${f}`}
                >
                  <span className="h-2 w-2 rounded-full" style={{ background: fam.color }} />
                  <span className="text-sm font-semibold" style={{ color: fam.badgeText }}>{fam.label}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-12">
            <button
              onClick={startLogin}
              data-testid="google-login-button"
              className="group inline-flex items-center gap-3 rounded-full bg-[#1F2421] px-6 py-3.5 text-white font-medium hover:bg-[#2D6A4F] focus:outline-none focus:ring-2 focus:ring-[#2D6A4F] focus:ring-offset-2 focus:ring-offset-[#F9F9F7]"
              style={{ transition: "background-color 0.25s ease" }}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z" />
                <path fill="#1976D2" d="M43.6 20.5H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.4l6.2 5.2C41.1 36.4 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z" />
              </svg>
              Continue with Google
              <ArrowRight className="h-4 w-4 opacity-70 group-hover:translate-x-0.5" style={{ transition: "transform 0.2s ease" }} />
            </button>
            <p className="mt-4 text-xs text-[#8C8C85]">Secure sign-in. We only use your name and email to set up your workspace.</p>
          </div>
        </div>

        <p className="text-xs text-[#8C8C85] font-mono">© {new Date().getFullYear()} OmniAI Studio</p>
      </div>

      {/* Right: visual panel */}
      <div className="hidden lg:block relative overflow-hidden bg-[#1F2421]">
        <div className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #2D6A4F 0, transparent 40%), radial-gradient(circle at 80% 60%, #1A73E8 0, transparent 35%)" }} />
        <div className="relative h-full flex items-center justify-center p-16">
          <div className="w-full max-w-md space-y-4">
            {[
              { f: "openai", t: "Explain quantum entanglement simply.", ai: true },
              { f: "anthropic", t: "Draft a launch email for our new app.", ai: false },
              { f: "gemini", t: "Generate a poster of a neon city.", ai: true },
            ].map((row, i) => {
              const fam = MODEL_FAMILIES[row.f];
              return (
                <div
                  key={i}
                  className={`omni-rise rounded-2xl p-4 ${row.ai ? "bg-white/95" : "bg-white/10 border border-white/15"}`}
                  style={{ animationDelay: `${i * 0.12}s` }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: fam.color }} />
                    <span className={`text-xs font-semibold ${row.ai ? "text-[#5F5F5B]" : "text-white/70"}`}>{fam.label}</span>
                  </div>
                  <p className={`text-sm ${row.ai ? "text-[#171716]" : "text-white/90"}`}>{row.t}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
